import { memo, useCallback, useEffect, useState } from 'react';

import { Button, Form, Input } from 'antd';

import { EMAIL_PLACEHOLDER } from '../../constants/common';
import messages from '../../constants/messages';

import AgreementField from '../../components/AgreementField';
import PasswordField from '../../components/PasswordField';

import OtpField from './OtpField';
import PhoneField from './PhoneField';
import { STEPS } from './constants';

function SignUpForm({ formRef, loading, step, handleNextStep, resendCode }) {
  const [submittable, setSubmittable] = useState(false);
  const [emailOtp, setEmailOtp] = useState([]);
  const [phoneOtp, setPhoneOtp] = useState([]);

  const [form] = Form.useForm();

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable?.(true))
      .catch(() => setSubmittable?.(false));
  }, [values]);

  const setOtp = useCallback((value, field) => {
    field === 'emailOtp' ? setEmailOtp(value) : setPhoneOtp(value);
  }, []);

  return (
    <Form form={form} id="signUpForm" layout="vertical" requiredMark={false} size="small">
      <Form.Item
        label="Email"
        name="email"
        colon={false}
        rules={[
          { required: true, message: messages.FIELD_REQUIRED('Email') },
          { type: 'email', message: messages.FIELD_INVALID('Email') }
        ]}>
        <Input size="large" addonBefore={<i className="fas fa-at"></i>} name="email" placeholder={EMAIL_PLACEHOLDER} />
      </Form.Item>

      <PasswordField
        name="password"
        rules={[
          { required: true, message: messages.FIELD_REQUIRED('Password') },
          { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/, message: messages.PASSWORD_INVALID }
        ]}
      />

      <PasswordField
        dependencies={['password']}
        label="Confirm Password"
        name="password_confirm"
        rules={[
          {
            required: true,
            message: messages.FIELD_REQUIRED('Confirm Password')
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              return getFieldValue('password') === value
                ? Promise.resolve()
                : Promise.reject(new Error(messages.PASSWORDS_MISMATCH));
            }
          })
        ]}
      />

      <AgreementField />

      {step === STEPS.EMAIL_OTP && (
        <OtpField
          label="Verify your email"
          name="emailOtp"
          onInput={value => setOtp(value, 'emailOtp')}
          resendCode={() => resendCode('emailOtp')}
        />
      )}
      {(step === STEPS.PHONE || step === STEPS.PHONE_OTP) && <PhoneField label="Phone" name="phone" />}
      {step === STEPS.PHONE_OTP && (
        <OtpField
          label="Verify your phone"
          name="phoneOtp"
          onInput={value => setOtp(value, 'phoneOtp')}
          resendCode={() => resendCode('phoneOtp')}
        />
      )}

      <Button
        className="w-full my-3"
        color="primary"
        size="large"
        variant="solid"
        disabled={
          !submittable ||
          loading ||
          (emailOtp?.length < 6 && step === STEPS.EMAIL_OTP) ||
          (phoneOtp?.length < 6 && step === STEPS.PHONE_OTP)
        }
        loading={loading}
        onClick={handleNextStep}>
        {loading ? 'Please wait...' : 'Continue'}
      </Button>
    </Form>
  );
}

export default memo(SignUpForm);
